import type { CsvRows } from './decodeCsvFile'

/** The header row is line 1, so the server numbers data rows from 2. */
const FIRST_DATA_LINE = 2

/** Only the characters PHP's trim() strips. String.prototype.trim also strips no-break and other Unicode spaces. */
const PHP_TRIM = /^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g

const phpTrim = (value: string): string => value.replace(PHP_TRIM, '')

/** Utf8Sanitizer's last steps: drop a leading BOM and turn U+FFFD into a space. */
const sanitize = (cell: string): string =>
  cell.replace(/^\uFEFF/, '').replace(/\uFFFD/g, ' ')

/** A cell as the importer reads it: sanitized, then trimmed by Mapping::get. */
export const serverCellValue = (cell: string | undefined): string =>
  phpTrim(sanitize(cell ?? ''))

/** BaseCSVImport::trimCSVHeaders: strtolower (ASCII only), trim, spaces to underscores. */
const normalizeHeader = (cell: string): string =>
  phpTrim(
    sanitize(cell).replace(/[A-Z]/g, (letter) => letter.toLowerCase())
  ).replace(/ /g, '_')

/**
 * The header and data rows of a decoded file, laid out the way the importer
 * numbers them.
 */
export interface ImportCsvLayout {
  /** The header row, normalized like the importer's. */
  header: string[]
  /** The index in the rows of each data row, in line order. */
  dataRows: number[]
}

/**
 * The importer drops a row whose cells are all empty (",,,") without giving it
 * a number. Papa keeps such rows, so line L is the (L - 2)th row that has a
 * value, not rows[L - 1].
 */
export const importCsvLayout = (rows: CsvRows): ImportCsvLayout => {
  const dataRows: number[] = []
  for (let index = 1; index < rows.length; index++) {
    if (rows[index].some((cell) => cell !== '')) {
      dataRows.push(index)
    }
  }

  return { header: (rows[0] ?? []).map(normalizeHeader), dataRows }
}

/** The index in the rows of the data row the importer numbered `line`, if there is one. */
export const rowIndexForLine = (
  layout: ImportCsvLayout,
  line: number
): number | undefined => layout.dataRows[line - FIRST_DATA_LINE]

/**
 * Where a column is, as Mapping::get finds it: the first header that matches,
 * then each alias in turn when the column itself is absent. -1 when none is there.
 */
export const columnIndex = (
  layout: ImportCsvLayout,
  slug: string,
  aliases: readonly string[] = []
): number => {
  for (const name of [slug, ...aliases]) {
    const index = layout.header.indexOf(name)
    if (index !== -1) {
      return index
    }
  }

  return -1
}

/** "Roster Name" is the column's old header (MultiRosterCsvImportMapping::ALIASES). */
const COMPETITION_NAME_ALIASES = ['roster_name']

export interface ImportRowLabels {
  teamName: string
  competitionName: string
  snPlayerId: string
}

/** The team, competition and SN Player ID a flagged line carries in the file. */
export const importRowLabels = (
  rows: CsvRows,
  layout: ImportCsvLayout,
  line: number
): ImportRowLabels => {
  const index = rowIndexForLine(layout, line)
  const row = index === undefined ? [] : rows[index]
  const cell = (column: number) =>
    column === -1 ? '' : serverCellValue(row[column])

  return {
    teamName: cell(columnIndex(layout, 'team_name')),
    competitionName: cell(
      columnIndex(layout, 'competition_name', COMPETITION_NAME_ALIASES)
    ),
    snPlayerId: cell(columnIndex(layout, 'sn_player_id')),
  }
}
